import { resolveAlertSeverity } from '../../constants/device-alerts';
import { useIntl } from '../../hooks';
import { useAcknowledgeDeviceAlert } from '../../hooks/useAcknowledgeDeviceAlert';
import { describeDeviceAlert, sortDeviceAlertsBySeverity } from '../../lib/device-alert-text';
import type { DeviceAlert } from '../../types';
import { Button } from '../ui';

type AcknowledgeAlertButtonProps = {
  deviceId: string;
  alertId: string;
};

// Its own component so the mutation, and the QueryClient it needs, mount only for roles that ack.
const AcknowledgeAlertButton = ({ deviceId, alertId }: AcknowledgeAlertButtonProps) => {
  const intl = useIntl();
  const { mutate, isPending } = useAcknowledgeDeviceAlert({
    deviceId,
    noLongerActiveMessage: intl.formatMessage({ id: 'alerts.acknowledge.noLongerActive' }),
    failedMessage: intl.formatMessage({ id: 'alerts.acknowledge.failed' }),
  });

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      data-test-id="device-alert-acknowledge"
      disabled={isPending}
      onClick={() => mutate({ alertId })}
    >
      {intl.formatMessage({ id: 'alerts.acknowledge.action' })}
    </Button>
  );
};

type DeviceAlertPopoverProps = {
  deviceId: string;
  deviceSerial: string;
  alerts: DeviceAlert[];
  canAcknowledge: boolean;
};

export const DeviceAlertPopover = ({
  deviceId,
  deviceSerial,
  alerts,
  canAcknowledge,
}: DeviceAlertPopoverProps) => {
  const intl = useIntl();

  return (
    <ul
      data-test-id="device-alert-popover"
      data-device-serial={deviceSerial}
      className="divide-y divide-border"
    >
      {sortDeviceAlertsBySeverity(alerts).map((deviceAlert) => (
        <li
          key={deviceAlert.id}
          data-test-id="device-alert-row"
          data-alert-id={deviceAlert.id}
          data-severity={resolveAlertSeverity(deviceAlert.severity)}
          className="flex flex-col gap-2 py-2 first:pt-0 last:pb-0"
        >
          <p data-test-id="device-alert-description" className="break-words">
            {describeDeviceAlert(intl, deviceAlert)}
          </p>
          {canAcknowledge && (
            <div className="flex justify-end">
              <AcknowledgeAlertButton deviceId={deviceId} alertId={deviceAlert.id} />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};
